import { Card, InputAdornment, Popover, Tooltip } from "@mui/material";
import TextField from "@mui/material/TextField";
import moment from 'moment';
import React, { useEffect } from "react";
import Calendar from "react-calendar";
import { UtilityService } from "../../../services/UtilityService";
import "../../theme/calendar.scss";
import ButtonComponent from "../button/button.component";
import ArrowTimePickerTimeComponent from "./arrow-time.component";
import { DateIcon, InformationDarkScheduleIcon, ResetIcon } from "../../../icon";
import { useSelector } from "react-redux";
import * as selector from "../../../selectors/selectors"
import setToast from "../../../helpers/toaster.helper";
import { ToasterTexts } from "../constants";
import { ToasterType } from "../toaster/toaster.model";

type DatePickerTab = "starts" | "ends";

interface Props {
	handleChange: (dateTime: number) => void;
	DateTime: number;
	IsMobileDevice?: boolean;
	DeviceWidth?: number;
	HideTime?: boolean;
	isMessage?: boolean;
	TextLabel?: string;
	renderDateTime?: () => JSX.Element;
	FormatText?: string;
	FormatTextLabel?: string;
	event?: any;
	anchorEl?: any;
	CloseBottomDrawer?: () => void;
	CloseCalender?: () => void;
	TextFiledMediumSize?: boolean;
	MaxDate?: boolean;
	ShowRestIcon?: boolean;
	ShowMinData?: boolean;
	/** Enables Starts / Ends tabs for schedule message range selection */
	ShowStartEndTabs?: boolean;
	EndDateTime?: number;
	handleEndChange?: (dateTime: number) => void;
}

const getInitialValue = (dateTime: number) => {
	const converted = UtilityService.convertToPreferredTimeZone(dateTime);
	const now = UtilityService.getPreferredTimeZoneTimeNow();
	return converted > now ? converted : now;
};

const getDefaultEndDraft = (start: Date) => new Date(start.getTime() + 60 * 60 * 1000);

export default function DateAndTimePicker(props: Props) {
	const hasExistingEnd = !!(props.EndDateTime && props.EndDateTime !== -1);
	const [value, setValue] = React.useState(getInitialValue(props.DateTime));
	const [endValue, setEndValue] = React.useState<Date | null>(
		hasExistingEnd ? getInitialValue(props.EndDateTime) : null
	);
	const [isEndSelected, setIsEndSelected] = React.useState(hasExistingEnd);
	const [activeTab, setActiveTab] = React.useState<DatePickerTab>("starts");
	const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
	const [openCalender, setOpenCalender] = React.useState<boolean>(false);
	const IsDarkMode: boolean = useSelector(selector.isDarkMode)
	const minuteStep = 10;
	const showStartEndTabs = !!props.ShowStartEndTabs;

	useEffect(() => {
		if (props.event !== undefined) {
			setAnchorEl(props.event.currentTarget);
			setOpenCalender(true);
		}
	}, [])

	useEffect(() => {
		if (props.DateTime && props.DateTime !== -1) {
			setValue(getInitialValue(props.DateTime));
		}
	}, [props.DateTime])

	useEffect(() => {
		if (props.EndDateTime && props.EndDateTime !== -1) {
			setEndValue(getInitialValue(props.EndDateTime));
			setIsEndSelected(true);
		} else if (props.EndDateTime === -1) {
			setEndValue(null);
			setIsEndSelected(false);
		}
	}, [props.EndDateTime])

	// Draft shown on Ends tab until the user selects; not committed until then.
	const endDisplayValue = endValue ?? getDefaultEndDraft(value);
	const activeValue = activeTab === "starts" ? value : endDisplayValue;

	const handleClick = (event: any) => {
		event.preventDefault();
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		if (props.event !== undefined) {
			setOpenCalender(false);
			props.CloseCalender();
		}
		setAnchorEl(null);
		setActiveTab("starts");
	};

	const handlePopoverClose = () => {
		if (props.event !== undefined) {
			setOpenCalender(false);
			props.CloseCalender();
		}
		setAnchorEl(null);
		setActiveTab("starts");
	};

	const commitActiveValue = (next: Date) => {
		if (activeTab === "starts") {
			setValue(next);
		} else {
			setEndValue(next);
			setIsEndSelected(true);
		}
	};

	const onChange = (date: any) => {
		let fDate = UtilityService.convertToDateFormat(date);
		let fTime = UtilityService.convertToTimeFormat(activeValue);
		let x = UtilityService.convertToDateTimeFormat(`${fDate} ${fTime}`)
		commitActiveValue(new Date(x));
		if (props.HideTime) {
			props.handleChange(UtilityService.roundTimeToMinutes(new Date(x).getTime(), minuteStep));
			handleClose();
		}
	}

	const open = Boolean(anchorEl);

	const handleTimeClick = (time: any) => {
		let fDate = UtilityService.convertToDateFormat(activeValue);
		let fTime = UtilityService.convertToTimeFormat(time);
		let x = UtilityService.convertToDateTimeFormat(`${fDate} ${fTime}`)
		commitActiveValue(new Date(x));
	}

	const trimTimeZone = (timeZoneName: any, maxLength: number) => {
		return !!timeZoneName ? timeZoneName.slice(0, maxLength) + (timeZoneName.length > maxLength ? "..." : "") : "";
	}

	const handleSave = () => {
		const startMs = UtilityService.roundTimeToMinutes(new Date(value).getTime(), minuteStep);
		if (showStartEndTabs) {
			if (isEndSelected && endValue) {
				const endMs = UtilityService.roundTimeToMinutes(new Date(endValue).getTime(), minuteStep);
				if (endMs <= startMs) {
					setToast(ToasterTexts.SCHEDULED_MESSAGE_END_BEFORE_START, ToasterType.DANGER);
					return;
				}
				props.handleChange(startMs);
				props.handleEndChange?.(endMs);
			} else {
				// No end selected → unlimited (-1)
				props.handleChange(startMs);
				props.handleEndChange?.(-1);
			}
		} else {
			props.handleChange(startMs);
		}
		setValue(UtilityService.getPreferredTimeZoneTimeNow())
		handleClose();
		if (props.IsMobileDevice && props.CloseBottomDrawer) {
			props.CloseBottomDrawer();
		}
	}

	const handleResetIcon = (event: any) => {
		event.stopPropagation()
		setAnchorEl(null);
		props.handleChange(-1)
		props.handleEndChange?.(-1)
	};

	const handleTabChange = (tab: DatePickerTab) => {
		setActiveTab(tab);
	};

	const handleClearEnd = (event: React.MouseEvent) => {
		event.stopPropagation();
		setEndValue(null);
		setIsEndSelected(false);
	};

	return (
		<>
			{props.event == undefined ? <TextField
				size={props.TextFiledMediumSize ? "medium" : "small"}
				fullWidth
				label={props.TextLabel}
				className="br-1"
				tabIndex={17}
				onClick={handleClick}
				value={props.DateTime === -1 ? (props.FormatTextLabel || "MM DD YYYY HH:mm") :
					moment(props.DateTime).format(props.FormatText || `MMMM Do YYYY HH:mm`)}
				InputProps={{
					classes: { input: 'h4-regular', notchedOutline: 'br-text-field w-fill' },
					startAdornment: (
						<InputAdornment position="start">
							<button tabIndex={17} data-up-arrow-index={300} onClick={handleClick}>
								<DateIcon className={`c-pointer d-flex align-center`} />
							</button>
						</InputAdornment>
					),
					endAdornment: (props.ShowRestIcon && props.DateTime !== -1 &&
						<InputAdornment position="end">
							<button tabIndex={17} data-up-arrow-index={300} onClick={handleResetIcon}>
								<ResetIcon className={`c-pointer align-center d-flex`}
								/>
							</button>
						</InputAdornment>
					),
				}}
				inputProps={{
					autocomplete: "off",
					style: {
						color: props.DateTime === -1 ? '#B3B3B3' : 'inherit'
					}
				}}
				variant="outlined"
			/> : <> </>}

			<Popover
				anchorOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				open={props.isMessage ? props.isMessage : props.event !== undefined ? openCalender : open}
				anchorEl={props.anchorEl ? props.anchorEl : props.event !== undefined ? props.event.current : anchorEl}
				onClose={handleClose}
				hideBackdrop={!props.HideTime}
				classes={{ paper: `${props.IsMobileDevice ? "min-w-30" : ""}` }}
			>
				<Card className={`${showStartEndTabs ? "d-flex flex-direction" : "d-flex"} ${props.IsMobileDevice ? props.DeviceWidth < 373 ? "py-1 flex-direction align-center" : "" : "py-1"} ${IsDarkMode ? "bg-color-sidebar-bg" : ""}`}>
					{showStartEndTabs && (
						<div className="d-flex flex-direction">
							<div className="mx-auto w-p-96 mb-1">
								<div className="d-flex border-primary-divider  br-2 bg-color-header-dropdown">
									<div className="h5-medium w-p-50 py-1 d-flex align-center justify-center">
										<div className={` w-p-80 br-2 py-1 t-align-center c-pointer ${activeTab === "starts" ? "color-primary-light bg-color-secondary" : "color-primary-accent"}`} onClick={() => handleTabChange("starts")}>Starts</div>
									</div>
									<div className="h5-medium w-p-50 py-1 d-flex align-center justify-center">
										<div className={` w-p-80 br-2 py-1 t-align-center c-pointer  ${activeTab === "ends" ? "color-primary-light bg-color-secondary" : "color-primary-accent"}`} onClick={() => handleTabChange("ends")}>Ends</div>
									</div>
								</div>
							</div>
						</div>
					)}

					<div className={`d-flex ${props.IsMobileDevice && props.DeviceWidth < 373 ? "flex-direction align-center" : ""}`}>
						<div className="d-flex flex-direction">
							<div className={`${IsDarkMode ? "bg-color-sidebar-bg" : ""}`}>
								<Calendar
									onClickDay={onChange}
									value={activeValue}
									minDate={
										showStartEndTabs && activeTab === "ends"
											? value
											: (!props.ShowMinData && !props.MaxDate && UtilityService.getPreferredTimeZoneTimeNow())
									}
									maxDate={props.MaxDate && UtilityService.getPreferredTimeZoneTimeNow()}
								/>
							</div>
							<div className="pt-1 pl-1 m-auto d-flex">
								<div className="m-auto">
									Account time zone: {trimTimeZone(UtilityService.getPreferedTimeZone().Name, 15)} ({UtilityService.getPreferedTimeZone().OffsetLabel})
								</div>
								<Tooltip title={"By default Scheduled messages are sent based on your account time zone."} placement="top-end"  >
									<InformationDarkScheduleIcon className="pl-1 m-auto" />
								</Tooltip>
							</div>
						</div>
						{!props.HideTime && <>
							<div className={"datetime-divider"} />
							<div className={` ${props.IsMobileDevice ? "d-flex flex-direction justify-item-center" : "px-1"}`}>
								<ArrowTimePickerTimeComponent
									key={showStartEndTabs ? `${activeTab}-${isEndSelected}` : "time"}
									handleChange={handleTimeClick}
									MinuteStep={minuteStep}
									HourStep={1}
									Value={showStartEndTabs ? activeValue.getTime() : props.DateTime}
									IsMobileDevice={props.IsMobileDevice}
								/>
								<ButtonComponent ButtonLabel={"Save"} ClassName={` p-0 h5-regular br-0 ${props.IsMobileDevice ? "w-p-80 mx-auto" : "w-p-100"}`} Variant={"primary-btn"} OnClick={handleSave}></ButtonComponent>
								<ButtonComponent ButtonLabel={"Cancel"} ClassName={` mt-1 p-0 h5-regular br-0 ${props.IsMobileDevice ? "w-p-80 mx-auto" : "w-p-100"}`} Variant={"secondary-btn-small"} OnClick={() => { handlePopoverClose() }}></ButtonComponent>
							</div>
						</>}
					</div>

				</Card>
			</Popover>
		</>
	);
}
